'use client';

import { useState, useEffect, useRef } from 'react';
import { Copy, Check, AlertCircle, HelpCircle, X } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { regexTesterContent } from '../../data/regex-tester-content';
import Link from 'next/link';

interface MatchResult {
  match: string;
  index: number;
  groups: Record<string, string>;
}

export default function RegexTester() {
  const [pattern, setPattern] = useState('');
  const [testString, setTestString] = useState('');
  const [flags, setFlags] = useState({
    g: false,
    i: false,
    m: false,
    s: false,
    u: false,
    y: false,
  });
  const [matches, setMatches] = useState<MatchResult[]>([]);
  const [error, setError] = useState('');
  const [showCheatSheet, setShowCheatSheet] = useState(false);
  const [copied, setCopied] = useState(false);
  const textareaRef = useRef<HTMLDivElement>(null);
  const patternInputRef = useRef<HTMLInputElement>(null);

  // Update matches when pattern, testString, or flags change
  useEffect(() => {
    if (!pattern) {
      setMatches([]);
      setError('');
      return;
    }

    try {
      const activeFlags = Object.entries(flags)
        .filter(([_, isActive]) => isActive)
        .map(([flag]) => flag)
        .join('');

      const regex = new RegExp(pattern, activeFlags);
      setError('');

      // Reset matches
      let newMatches: MatchResult[] = [];

      // Find all matches
      const matchIterator = testString.matchAll(regex);
      for (const match of matchIterator) {
        newMatches.push({
          match: match[0],
          index: match.index || 0,
          groups: match.groups || {},
        });
      }

      setMatches(newMatches);
    } catch (err) {
      setError((err as Error).message);
      setMatches([]);
    }
  }, [pattern, testString, flags]);

  const handleFlagToggle = (flag: keyof typeof flags) => {
    setFlags(prev => ({
      ...prev,
      [flag]: !prev[flag]
    }));
  };

  const copyToClipboard = (text: string) => {
    if (!text) return;

    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      toast.success('Copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const clearAll = () => {
    setPattern('');
    setTestString('');
    setMatches([]);
    setError('');
  };

  const insertSample = (sample: string) => {
    setTestString(sample);
    toast.success('Sample text inserted!');
  };

  const getHighlightedText = () => {
    if (!pattern || !testString) return testString;

    try {
      const activeFlags = Object.entries(flags)
        .filter(([_, isActive]) => isActive)
        .map(([flag]) => flag)
        .join('');

      const regex = new RegExp(`(${pattern})`, `g${activeFlags}`);
      const parts = testString.split(regex);

      return parts.map((part, i) => {
        const isMatch = i % 2 === 1;
        return isMatch ? (
          <mark key={i} className="bg-yellow-200 text-gray-900 px-0.5 rounded">
            {part}
          </mark>
        ) : (
          <span key={i}>{part}</span>
        );
      });
    } catch (err) {
      return testString;
    }
  };

  const regexCheatSheet = [
    {
      title: 'Character Classes',
      items: [
        { pattern: '.', description: 'Any character except newline' },
        { pattern: '\d', description: 'Digit (0-9)' },
        { pattern: '\D', description: 'Not a digit (0-9)' },
        { pattern: '\w', description: 'Word character (a-z, A-Z, 0-9, _)' },
        { pattern: '\W', description: 'Not a word character' },
        { pattern: '\s', description: 'Whitespace (space, tab, newline)' },
        { pattern: '\S', description: 'Not whitespace' },
      ],
    },
    {
      title: 'Anchors',
      items: [
        { pattern: '^', description: 'Start of string' },
        { pattern: '$', description: 'End of string' },
        { pattern: '\b', description: 'Word boundary' },
      ],
    },
    {
      title: 'Quantifiers',
      items: [
        { pattern: '*', description: '0 or more' },
        { pattern: '+', description: '1 or more' },
        { pattern: '?', description: '0 or 1' },
        { pattern: '{3}', description: 'Exactly 3' },
        { pattern: '{3,}', description: '3 or more' },
        { pattern: '{3,5}', description: '3, 4, or 5' },
      ],
    },
    {
      title: 'Groups',
      items: [
        { pattern: '(...)', description: 'Capturing group' },
        { pattern: '(?<name>...)', description: 'Named capturing group' },
        { pattern: '(?:...)', description: 'Non-capturing group' },
        { pattern: '|', description: 'OR' },
      ],
    },
  ];

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6">
      <div className="mb-8">
        <ToolPageIntro title="Regex Tester">
                Test and debug regular expressions with our free online tool. Validate regex patterns against test strings and get instant matching results.
            </ToolPageIntro>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="space-y-4">
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <label htmlFor="pattern" className="block text-sm font-medium text-gray-700">
                Regular Expression
              </label>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setShowCheatSheet(!showCheatSheet)}
                  className="text-gray-500 hover:text-gray-700"
                  title="Show cheat sheet"
                >
                  <HelpCircle className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div className="relative">
              <div className="flex rounded-md shadow-sm">
                <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm">
                  /
                </span>
                <input
                  ref={patternInputRef}
                  id="pattern"
                  type="text"
                  value={pattern}
                  onChange={(e) => setPattern(e.target.value)}
                  className="flex-1 min-w-0 block w-full px-3 py-2 rounded-none rounded-r-md border border-gray-300 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Enter your regex pattern"
                  spellCheck="false"
                />
              </div>
              {error && (
                <div className="mt-1 text-sm text-red-600 flex items-start">
                  <AlertCircle className="h-4 w-4 mr-1 mt-0.5 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <label htmlFor="testString" className="block text-sm font-medium text-gray-700">
                Test String
              </label>
              <div className="flex space-x-2">
                <button
                  onClick={() => insertSample('Hello, my email is user@example.com and my phone is (123) 456-7890.')}
                  className="text-xs text-blue-600 hover:text-blue-800"
                >
                  Sample Text
                </button>
                <button
                  onClick={clearAll}
                  className="text-xs text-gray-600 hover:text-gray-800"
                >
                  Clear All
                </button>
              </div>
            </div>
            <div className="relative">
              <div
                ref={textareaRef as any}
                className="w-full h-64 p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white overflow-auto"
                style={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}
                contentEditable
                onInput={(e) => setTestString(e.currentTarget.textContent || '')}
                suppressContentEditableWarning
              >
                {testString}
              </div>
              <div
                className="absolute inset-0 p-3 pointer-events-none overflow-auto"
                style={{
                  whiteSpace: 'pre-wrap',
                  fontFamily: 'monospace',
                  zIndex: 10,
                  backgroundColor: 'transparent',
                  color: 'transparent',
                  caretColor: 'black',
                }}
              >
                {getHighlightedText()}
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="block text-sm font-medium text-gray-700">
                Flags
              </span>
              <span className="text-xs text-gray-500">
                {Object.entries(flags)
                  .filter(([_, isActive]) => isActive)
                  .map(([flag]) => flag)
                  .join('') || 'None'}
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {Object.keys(flags).map((flag) => (
                <button
                  key={flag}
                  type="button"
                  onClick={() => handleFlagToggle(flag as keyof typeof flags)}
                  className={`px-3 py-1.5 text-sm rounded-md ${flags[flag as keyof typeof flags]
                    ? 'bg-blue-100 text-blue-800 border border-blue-200'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  title={{
                    g: 'Global: Test against all possible matches',
                    i: 'Case-insensitive: Case is ignored',
                    m: 'Multiline: ^ and $ match start/end of line',
                    s: 'Dot all: . matches newline',
                    u: 'Unicode: Treat pattern as Unicode',
                    y: 'Sticky: Matches only from lastIndex position',
                  }[flag]}
                >
                  /{flag}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-medium text-gray-900">Match Information</h3>
              <div className="text-sm text-gray-500">
                {matches.length} {matches.length === 1 ? 'match' : 'matches'} found
              </div>
            </div>

            {matches.length > 0 ? (
              <div className="border border-gray-200 rounded-md overflow-hidden">
                <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
                  <div className="grid grid-cols-3 text-xs font-medium text-gray-500">
                    <div>Match</div>
                    <div>Position</div>
                    <div>Length</div>
                  </div>
                </div>
                <div className="divide-y divide-gray-200 max-h-[400px] overflow-y-auto">
                  {matches.map((match, index) => (
                    <div key={index} className="p-4 hover:bg-gray-50">
                      <div className="grid grid-cols-3 text-sm">
                        <div className="font-mono text-sm break-all">{match.match}</div>
                        <div className="text-gray-600">{match.index}</div>
                        <div className="flex justify-between items-center">
                          <span>{match.match.length}</span>
                          <button
                            onClick={() => copyToClipboard(match.match)}
                            className="text-gray-400 hover:text-gray-600"
                            title="Copy to clipboard"
                          >
                            {copied ? (
                              <Check className="h-4 w-4 text-green-500" />
                            ) : (
                              <Copy className="h-4 w-4" />
                            )}
                          </button>
                        </div>
                      </div>
                      {Object.keys(match.groups).length > 0 && (
                        <div className="mt-2 pt-2 border-t border-gray-100">
                          <div className="text-xs text-gray-500 mb-1">Groups:</div>
                          <div className="grid grid-cols-2 gap-2 text-xs">
                            {Object.entries(match.groups).map(([name, value], i) => (
                              <div key={i} className="bg-gray-50 p-1.5 rounded">
                                <div className="font-medium text-gray-700">{name}:</div>
                                <div className="font-mono break-all">{value}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                <div className="text-gray-400">
                  {pattern
                    ? 'No matches found'
                    : 'Enter a regular expression to see matches here'}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {showCheatSheet && (
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">Regex Cheat Sheet</h3>
            <button
              onClick={() => setShowCheatSheet(false)}
              className="text-gray-400 hover:text-gray-500"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {regexCheatSheet.map((section, i) => (
              <div key={i} className="space-y-2">
                <h4 className="font-medium text-gray-900">{section.title}</h4>
                <div className="space-y-1">
                  {section.items.map((item, j) => (
                    <div key={j} className="flex items-start">
                      <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono mr-2">
                        {item.pattern}
                      </code>
                      <span className="text-sm text-gray-600">{item.description}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
        <h3 className="font-medium text-blue-800 mb-2">How to use the Regex Tester</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <h4 className="font-medium text-blue-700 mb-1">Basic Usage:</h4>
            <ol className="list-decimal list-inside text-sm text-blue-700 space-y-1">
              <li>Enter your regular expression in the input field</li>
              <li>Type or paste your test string</li>
              <li>View matches in real-time</li>
              <li>Use flags to modify matching behavior</li>
            </ol>
          </div>
          <div>
            <h4 className="font-medium text-blue-700 mb-1">Tips:</h4>
            <ul className="list-disc list-inside text-sm text-blue-700 space-y-1">
              <li>Use ^ and $ to match start/end of string</li>
              <li>Use ( ) for capturing groups</li>
              <li>Use | for OR conditions</li>
              <li>Click the help icon for a regex cheat sheet</li>
            </ul>
          </div>
        </div>

        {/* Informational Content */}
        <InformationalContent
          title={regexTesterContent.title}
          description={regexTesterContent.description}
          content={regexTesterContent.content}
          faqs={regexTesterContent.faqs}
          relatedTools={regexTesterContent.relatedTools}
        />
      </div>
    </div>
  );
}
